import React, { forwardRef, KeyboardEvent, MouseEvent, PropsWithChildren, ReactNode, useRef } from 'react'
import clsx from 'clsx'
import { SideNav as SideNavStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import SideNavSection from './SideNavSection'
import uniqueId from 'lodash.uniqueid'
import { CommonProps } from '../helperTypes'

const useStyles = createUseStyles(SideNavStyles, 'sidenav')

type Props = PropsWithChildren<{
  /** to be used on sub categories to visually highlight the one currently navigating into */
  current?: boolean
  /** Disabled state */
  disabled?: boolean
  /** optional href url */
  href?: string
  /** display label */
  label?: string | ReactNode
  /** handler dispatched when the item is selected */
  onSelected?: (href?: string) => void
  /** display childs if true */
  open?: boolean
  /** Selected state */
  selected?: boolean
}>

export type SideNavCategoryProps = Props & CommonProps

export const SideNavCategory = (props: SideNavCategoryProps, ref: React.Ref<HTMLLIElement>) => {
  const {
    children,
    className,
    current = false,
    disabled = false,
    href,
    id: propsId,
    label,
    onSelected,
    open,
    selected = false,
    ...rest
  } = props
  const classes = useStyles()
  const defaultId = useRef<string>(uniqueId('SideNavCategory-'))
  const id = propsId || defaultId.current

  const dispatchSelect = (evt: KeyboardEvent | MouseEvent, stopPropagation: boolean = false) => {
    if (stopPropagation) {
      evt.stopPropagation()
      return
    }
    if (onSelected) {
      evt.preventDefault()
      onSelected(href)
    }
  }
  return (
    <li
      {...rest}
      ref={ref}
      className={clsx(
        className,
        classes.category,
        !open && classes.category_closed,
        open && classes.category_opened,
        current && classes.category_current,
        disabled && classes.item_disabled,
        selected && classes.category_selected,
      )}
    >
      <a
        className={classes.itemLink}
        href={href || '#'}
        onKeyDown={(evt: KeyboardEvent) => {
          if (evt.key === ' ' || evt.key === 'Enter') dispatchSelect(evt, disabled)
        }}
        onClick={(evt: MouseEvent) => dispatchSelect(evt, disabled)}
        aria-expanded={open}
        aria-controls={id}
      >
        {label}
      </a>
      <SideNavSection id={id}>{children}</SideNavSection>
    </li>
  )
}

export default forwardRef<HTMLLIElement, SideNavCategoryProps>(SideNavCategory)
